import { useState, useEffect } from 'react';
import { fetchCmsGallery } from '../lib/cms';
import Lightbox from './Lightbox';

function optimizeImg(url, width = 600) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace(
      'https://assets.auxodev.dk/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`
    );
  }
  return url;
}

export default function GallerySection({
  gallerySlug = 'baadtransport-galleri',
  title = 'Galleri & Billeder fra vandet',
  subtitle = 'Glimt fra udførte transporter og sejlads i de danske farvande'
}) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchCmsGallery(gallerySlug).then((data) => {
      if (isMounted) {
        setGallery(data);
        setLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [gallerySlug]);

  const images = gallery?.images || [];

  const openLightbox = (index) => {
    setActivePhotoIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center items-center text-slate-400">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-cyan-600 mr-3" aria-hidden="true" />
        <span className="text-sm">Henter galleri fra CMS...</span>
      </div>
    );
  }

  if (!images || images.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 bg-white" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
          <span className="text-cyan-600 font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 block">
            {gallery?.title || title}
          </span>
          <h2 id="gallery-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
            {gallery?.description || subtitle}
          </h2>
          <div className="w-16 h-1 bg-cyan-600 mx-auto mt-4 rounded-full" />
        </div>

        {/* Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images.map((imgUrl, idx) => (
            <button
              key={`${imgUrl}-${idx}`}
              type="button"
              onClick={() => openLightbox(idx)}
              className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 shadow-sm border border-slate-200/80 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-left transition-all hover:shadow-xl hover:scale-[1.01]"
              aria-label={`Åbn billede ${idx + 1} i fuld størrelse`}
            >
              <img
                src={optimizeImg(imgUrl, 600)}
                alt={`Galleri billede ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                width="600"
                height="450"
              />

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-maritime-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                <span className="w-11 h-11 rounded-full bg-white/90 text-maritime-950 flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <i className="fa-solid fa-magnifying-glass-plus text-base text-cyan-700" aria-hidden="true" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        images={images}
        currentIndex={activePhotoIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(newIdx) => setActivePhotoIndex(newIdx)}
      />
    </section>
  );
}
